import React, { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import {
  Alert, Box, Button, Card, CardContent, Container, Stack, TextField, Typography,
  CircularProgress, InputAdornment, IconButton, useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';
import { loginWithPassword, forgotPassword } from '../services/authService';
import { validateEmail, validatePassword, sanitizeString } from '../lib/validation';
import { throttle } from '../lib/rateLimiting';
import { addBreadcrumb } from '../lib/errorReporting';

const MotionContainer = motion(Container);

export default function Login() {
  const theme = useTheme();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromParam = params.get('from');
  const from = fromParam ? decodeURIComponent(fromParam) : '/';

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState({});

  // Forgot password dialog
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Throttled submit function to prevent rapid submissions
  const throttledSubmitRef = useRef(
    throttle(async (submitFn) => {
      await submitFn();
    }, 2000)
  );

  // Validate email on blur
  const handleEmailBlur = useCallback(() => {
    const result = validateEmail(email);
    setFieldErrors(prev => ({ ...prev, email: result.isValid ? null : result.error }));
  }, [email]);

  // Validate password on blur
  const handlePasswordBlur = useCallback(() => {
    const result = validatePassword(password, {
      minLength: 6,
      requireUppercase: false,
      requireLowercase: false,
      requireNumber: false
    });
    setFieldErrors(prev => ({ ...prev, password: result.isValid ? null : result.error }));
  }, [password]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password, {
      minLength: 6,
      requireUppercase: false,
      requireLowercase: false,
      requireNumber: false
    });

    const newFieldErrors = {};
    if (!emailValidation.isValid) newFieldErrors.email = emailValidation.error;
    if (!passwordValidation.isValid) newFieldErrors.password = passwordValidation.error;

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      return;
    }

    throttledSubmitRef.current(async () => {
      setLoading(true);
      try {
        addBreadcrumb({ category: 'auth', message: 'Login attempt', level: 'info' });
        const data = await loginWithPassword({ email: sanitizeString(email), password });

        if (data.success && data.user) {
          flushSync(() => {
            login({ user: data.user });
          });
          navigate(from, { replace: true });
        } else {
          setError(data.error || 'Login failed. Please try again.');
        }
      } catch (err) {
        setError(err.message || 'Login failed. Please check your credentials.');
        addBreadcrumb({ category: 'auth', message: 'Login failed', level: 'warning', data: { error: err.message } });
      } finally {
        setLoading(false);
      }
    });
  };

  const handleForgotPassword = async () => {
    const emailValidation = validateEmail(forgotEmail);
    if (!emailValidation.isValid) {
      setForgotError(emailValidation.error);
      return;
    }
    setForgotError('');
    setForgotSent(false);
    setForgotLoading(true);
    try {
      const resetContinueUrl = typeof window !== 'undefined'
        ? `${window.location.origin.replace(/\/$/, '')}/reset-password?platform=web`
        : undefined;

      await forgotPassword(sanitizeString(forgotEmail), resetContinueUrl);
      setForgotSent(true);
      addBreadcrumb({ category: 'auth', message: 'Password reset requested', level: 'info' });
    } catch (err) {
      setForgotError(err.message || 'Failed to send password reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotClose = () => {
    setForgotOpen(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSent(false);
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 80px)',
        display: 'flex',
        alignItems: 'center',
        background: 'radial-gradient(circle at 10% 20%, rgba(255, 215, 0, 0.1) 0%, rgba(255, 255, 255, 0) 42%), radial-gradient(circle at 90% 80%, rgba(75, 0, 130, 0.07) 0%, rgba(255, 255, 255, 0) 42%)'
      }}
    >
      <MotionContainer
        maxWidth="sm"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: '16px',
            boxShadow: '0 40px 80px rgba(0,0,0,0.06)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            overflow: 'visible'
          }}
        >
          <CardContent sx={{ p: { xs: 4, sm: 8 } }}>
            <Stack spacing={4}>
              <Stack spacing={1} textAlign="center">
                <Typography variant="h4" fontWeight={800}>Welcome Back</Typography>
                <Typography color="text.secondary">Enter your credentials to access your dashboard</Typography>
              </Stack>

              {error && (
                <Alert severity="error" variant="standard" sx={{ borderRadius: '16px' }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={3}>
                  <TextField
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email}
                    fullWidth
                    autoComplete="email"
                    autoFocus
                    disabled={loading}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                  />

                  <Box>
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={handlePasswordBlur}
                      error={Boolean(fieldErrors.password)}
                      helperText={fieldErrors.password}
                      fullWidth
                      autoComplete="current-password"
                      disabled={loading}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={loading}>
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => setForgotOpen(true)}
                        sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'none' }}
                        disabled={loading}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    sx={{
                      py: 1.8,
                      borderRadius: '16px',
                      fontSize: '1rem',
                      fontWeight: 700,
                      boxShadow: '0 10px 20px rgba(255, 215, 0, 0.3)'
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In to Questguru'}
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Don't have an account?{' '}
                  <Link
                    component={RouterLink}
                    to="/signup"
                    sx={{ fontWeight: 700, color: 'primary.main', textDecoration: 'none' }}
                  >
                    Create one for free
                  </Link>
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </MotionContainer>

      <Dialog
        open={forgotOpen}
        onClose={handleForgotClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We'll send a secure password reset link to your registered email address.
          </Typography>
          <TextField
            label="Email Address"
            type="email"
            value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
            disabled={forgotLoading || forgotSent}
            autoComplete="email"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
          />
          {forgotError && <Alert severity="error" sx={{ mt: 2, borderRadius: '16px' }}>{forgotError}</Alert>}
          {forgotSent && (
            <Alert severity="success" sx={{ mt: 2, borderRadius: '16px' }}>
              Reset link sent! Please check your inbox.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleForgotClose} disabled={forgotLoading} sx={{ fontWeight: 600 }}>
            {forgotSent ? 'Done' : 'Cancel'}
          </Button>
          {!forgotSent && (
            <Button
              onClick={handleForgotPassword}
              disabled={forgotLoading || !forgotEmail}
              variant="contained"
              sx={{ borderRadius: '16px', fontWeight: 700 }}
            >
              {forgotLoading ? <CircularProgress size={20} /> : 'Send Reset Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
